import Phaser from 'phaser';
import { sfx } from '../audio/SFX.js';

interface HowToPlayInit {
  next: string;
  nextData?: Record<string, unknown>;
}

interface StepDef {
  title: string;
  body: string;
}

const COUNT_KEY = 'snake_howto_count';

const STEPS: StepDef[] = [
  { title: 'PICK',          body: 'Choose 1 perk before the run' },
  { title: 'COLLECT',       body: 'Grab coins to earn points' },
  { title: 'SURVIVE',       body: 'Avoid walls and enemy snakes' },
  { title: 'EXTRACT',       body: 'Reach the portal to secure your run' },
  { title: 'EARN REWARDS',  body: 'Chests can contain coins or tokens' },
  { title: 'SPLIT THE POT', body: 'More points, bigger share of pool' },
];

export class HowToPlayScene extends Phaser.Scene {
  private next: string = 'MainMenuScene';
  private nextData: Record<string, unknown> | undefined;
  private dismissed: boolean = false;

  constructor() {
    super({ key: 'HowToPlayScene' });
  }

  init(data: HowToPlayInit): void {
    this.next = data?.next ?? 'MainMenuScene';
    this.nextData = data?.nextData;
    this.dismissed = false;
  }

  preload(): void {
    for (let i = 1; i <= STEPS.length; i++) {
      const key = `tutorial_${i}`;
      if (!this.textures.exists(key)) {
        this.load.image(key, `assets/sprites/tutorial/${i}.png`);
      }
    }
  }

  create(): void {
    const { width, height } = this.scale;
    const cx = width / 2;

    // === Dim background — click outside cells to dismiss ===
    const dim = this.add.rectangle(0, 0, width, height, 0x000000, 0.93)
      .setOrigin(0, 0)
      .setInteractive();
    dim.on('pointerdown', () => this.dismiss());

    // === Title ===
    this.add.text(cx, 30, 'HOW TO PLAY', {
      fontFamily: 'monospace',
      fontSize: '22px',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#000', strokeThickness: 5,
    }).setOrigin(0.5);

    // === Close × icon (top-right) ===
    const closeBtn = this.add.text(width - 22, 24, '×', {
      fontFamily: 'monospace',
      fontSize: '30px',
      color: '#888899',
      fontStyle: 'bold',
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    this.styleMenuText(closeBtn, '#ffffff');
    closeBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
      ev.stopPropagation();
      sfx(this, 'menu_portal_click');
      this.dismiss();
    });

    // === 6-step grid (3 cols × 2 rows), no frames ===
    const gridTop = 64;
    const gridBottom = height - 90; // leave room for Continue button
    const gridLeft = 18;
    const gridRight = width - 18;

    const cols = 3;
    const rows = 2;
    const cellW = (gridRight - gridLeft) / cols;
    const cellH = (gridBottom - gridTop) / rows;

    for (let i = 0; i < STEPS.length; i++) {
      const col = i % cols;
      const row = Math.floor(i / cols);
      const cellCx = gridLeft + col * cellW + cellW / 2;
      const cellCy = gridTop + row * cellH + cellH / 2;
      this.makeCell(cellCx, cellCy, cellW, cellH, i + 1, STEPS[i]);
    }

    // === Bottom action button — menu-portal-style ===
    const actionBtn = this.add.text(cx, height - 38, '[ CONTINUE ]', {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000', strokeThickness: 5,
    }).setOrigin(0.5).setInteractive({ useHandCursor: true });
    this.styleMenuText(actionBtn, '#ffd700');
    actionBtn.on('pointerdown', (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
      ev.stopPropagation();
      sfx(this, 'menu_portal_click');
      this.dismiss();
    });

    // === Keyboard ===
    const kb = this.input.keyboard;
    if (kb) {
      kb.once('keydown-ENTER', () => this.dismiss());
      kb.once('keydown-ESC', () => this.dismiss());
      kb.once('keydown-SPACE', () => this.dismiss());
    }

    // Subtle fade-in
    this.cameras.main.setAlpha(0);
    this.tweens.add({
      targets: this.cameras.main,
      alpha: 1,
      duration: 220,
      ease: 'Sine.easeOut',
    });
  }

  /** One step cell: number + title at top, image centered, body underneath.
   *  No frames — just text and the image floating on the dim background. */
  private makeCell(cx: number, cy: number, w: number, h: number, num: number, def: StepDef): void {
    const padX = 8;
    const innerW = w - padX * 2;

    // Header: "1.  PICK" (number gold, title white)
    const headerY = cy - h / 2 + 18;

    const numText = this.add.text(0, 0, `${num}.`, {
      fontFamily: 'monospace',
      fontSize: '24px',
      color: '#ffd700',
      fontStyle: 'bold',
      stroke: '#000', strokeThickness: 5,
    }).setOrigin(0, 0.5);

    const titleText = this.add.text(0, 0, def.title, {
      fontFamily: 'monospace',
      fontSize: '22px',
      color: '#ffffff',
      fontStyle: 'bold',
      stroke: '#000', strokeThickness: 5,
    }).setOrigin(0, 0.5);

    const headerW = numText.width + 6 + titleText.width;
    let hx = cx - headerW / 2;
    numText.setPosition(hx, headerY);   hx += numText.width + 6;
    titleText.setPosition(hx, headerY);

    // Image — centered in cell
    const imgKey = `tutorial_${num}`;
    let imgBottomY = cy;
    if (this.textures.exists(imgKey)) {
      const img = this.add.image(cx, cy, imgKey).setOrigin(0.5);
      // Cell-internal budget: ~60% of cell height for image
      const maxImgW = innerW * 0.85;
      const maxImgH = h * 0.45;
      const s = Math.min(maxImgW / img.width, maxImgH / img.height, 1.5);
      img.setScale(s);
      imgBottomY = cy + img.displayHeight / 2;
    }

    // Body — single line below image
    this.add.text(cx, imgBottomY + 14, def.body, {
      fontFamily: 'monospace',
      fontSize: '11px',
      color: '#cfd3e0',
      align: 'center',
      stroke: '#000', strokeThickness: 3,
      wordWrap: { width: innerW },
    }).setOrigin(0.5, 0);
  }

  /** Hover lift + color swap, mimicking the menu portal labels. */
  private styleMenuText(t: Phaser.GameObjects.Text, hoverColor: string): void {
    const baseY = t.y;
    const baseColor = t.style.color as string;
    t.on('pointerover', () => {
      sfx(this, 'menu_portal_hover');
      t.setColor(hoverColor);
      this.tweens.add({ targets: t, y: baseY - 4, scale: 1.06, duration: 180, ease: 'Power2' });
    });
    t.on('pointerout', () => {
      t.setColor(baseColor);
      this.tweens.add({ targets: t, y: baseY, scale: 1, duration: 180, ease: 'Power2' });
    });
  }

  private dismiss(): void {
    if (this.dismissed) return;
    this.dismissed = true;
    this.bumpCount();
    this.scene.start(this.next, this.nextData ?? {});
  }

  private bumpCount(): void {
    try {
      const n = Number(localStorage.getItem(COUNT_KEY) || '0');
      localStorage.setItem(COUNT_KEY, String(n + 1));
    } catch {
      /* ignore */
    }
  }
}
